import { Card, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "components/Tables/DataTable/DataTable";
import { Job } from "generated/graphql";
import { Link } from "react-router-dom";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

const toNumber = (value?: number | string | null) => Number(value ?? 0);

const renderTextCell = (value: string, isTotal = false) => (
  <MDTypography variant="button" fontWeight={isTotal ? "medium" : "regular"}>
    {value}
  </MDTypography>
);

const renderCurrencyCell = (value: number | string, isTotal = false) => (
  <MDTypography variant="button" fontWeight={isTotal ? "medium" : "regular"}>
    {formatCentsToUSD(value)}
  </MDTypography>
);

export default function JobCostSummarySection({
  job,
}: {
  job: Job;
}) {
  const columns = [
    {
      Header: "Phase",
      accessor: "name",
      width: "20%",
      Cell: (props) => renderTextCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "",
      accessor: "type",
      width: "20%",
      Cell: (props) => renderTextCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Total",
      accessor: "total",
      width: "20%",
      Cell: (props) =>
        props.value !== "" && props.value !== null && props.value !== undefined
          ? renderCurrencyCell(props.value, Boolean(props.row.original.isTotal))
          : "",
    },
    {
      Header: "Expense",
      accessor: "expense",
      width: "20%",
      Cell: (props) => renderCurrencyCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Profit",
      accessor: "profit",
      width: "20%",
      Cell: (props) => renderCurrencyCell(props.value, Boolean(props.row.original.isTotal)),
    },
    {
      Header: "Profit Margin",
      accessor: "profitMargin",
      width: "10%",
      Cell: (props) => renderTextCell(`${props.value.toFixed(2)}%`, Boolean(props.row.original.isTotal)),
    },
  ];

  // See https://lucid.app/lucidchart/599eabbb-9b60-4641-9461-67a3be617924/edit?invitationId=inv_c32d2ca4-69b4-4e85-856f-1518a3d35ccd&page=0_0#
  // https://app.clickup.com/t/863g1wern
  // for notes on calculation
  // Please consult the links above before making changes
  const buildTableRows = () => {
    const tableRows = [];
    job.jobPhases.forEach((phase) => {
      tableRows.push({
        name: phase.proposalProduct.material.name,
        type: "Proposed",
        total: phase.proposedOverallTotal,
        expense: phase.proposedCostTotal,
        profit: phase.proposedProfit,
        profitMargin:
          toNumber(phase.proposedOverallTotal) > 0
            ? (toNumber(phase.proposedProfit) / toNumber(phase.proposedOverallTotal)) * 100
            : 0,
      });
      tableRows.push({
        name: "",
        type: "Actuals",
        total: "",
        expense: toNumber(phase.actualCostTotal),
        profit: toNumber(phase.proposedOverallTotal) - toNumber(phase.actualCostTotal),
        profitMargin:
          toNumber(phase.proposedOverallTotal) > 0
            ? ((toNumber(phase.proposedOverallTotal) - toNumber(phase.actualCostTotal)) /
                toNumber(phase.proposedOverallTotal)) *
              100
            : 0,
      });
    });

    const actualProfit = toNumber(job.proposedOverallTotal) - toNumber(job.actualCostTotal);
    tableRows.push({
      name: "Total Job",
      type: "Proposed",
      total: toNumber(job.proposedOverallTotal),
      expense: toNumber(job.proposedCostTotal),
      profit: toNumber(job.proposedProfit),
      profitMargin:
        toNumber(job.proposedOverallTotal) > 0
          ? (toNumber(job.proposedProfit) / toNumber(job.proposedOverallTotal)) * 100
          : 0,
      isTotal: true,
    });
    tableRows.push({
      name: "",
      type: "Actuals",
      total: "",
      expense: toNumber(job.actualCostTotal),
      profit: actualProfit,
      profitMargin:
        toNumber(job.proposedOverallTotal) > 0
          ? (actualProfit / toNumber(job.proposedOverallTotal)) * 100
          : 0,
      isTotal: true,
    });

    return tableRows;
  };

  const tableData = { columns, rows: buildTableRows() };

  return (
    <Card>
      <MDBox mt={2} p={2}>
        <MDBox
          mb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <MDTypography>Job Cost Summary</MDTypography>
          <MDButton
            component={Link}
            to={`/organization/report/job_cost_detail?jobId=${job.id}`}
            target="_blank"
            rel="noreferrer"
            color="info"
            variant="outlined"
            size="small"
          >
            View Full Job Cost
          </MDButton>
        </MDBox>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <DataTable table={tableData} showTotalEntries={false} compact contentPaddingX={1.5} />
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}
